import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowsProp,
  GridRowId,
  GridRowParams,
  GridExperimentalFeatures,
  GridSortDirection,
} from '@mui/x-data-grid';
import { itIT } from '@mui/x-data-grid/locales';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { formatNumberItalian, formatNumberItalianSignificant } from '../utils/numberFormat';
import { convertToCSV, downloadCSV } from '../utils/csvExport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export interface ReadOnlyField {
  field: string;
  headerName: string;
  width?: number;
  type?: 'string' | 'number' | 'date' | 'singleSelect' | 'dateTime';
  align?: 'left' | 'right' | 'center';
  headerAlign?: 'left' | 'right' | 'center';
  valueFormatter?: (value: any) => string;
  renderCell?: (params: any) => React.ReactNode;
  sticky?: 'left' | 'right'; // Supporto per colonne fisse
  stickyLeft?: number; // Posizione left in px per colonne sticky (default: 0)
  valueOptions?: any[]; // Opzioni per singleSelect
  /** Numero di decimali per colonne number (politica formattazione griglie). Se non impostato usa formattazione default. */
  decimalPlaces?: number;
  /** Cifre significative (es. Indicatori: 3 → 0,000XXX). Ha precedenza su decimalPlaces se impostato. */
  significantDigits?: number;
}

export interface RowAction {
  label: string;
  icon: React.ReactElement;
  onClick: (id: GridRowId, row: any) => void;
  color?: 'inherit' | 'primary' | 'default';
  showInMenu?: boolean;
  /** If set, action is only shown when this returns true (e.g. "Sposta su" only when not first row) */
  visible?: (row: any) => boolean;
}

export interface ReadOnlyDataGridProps {
  fields: ReadOnlyField[];
  data: any[];
  actions?: RowAction[];
  /** Azioni per riga (ha precedenza su `actions` se fornito) */
  getRowActions?: (row: any) => RowAction[];
  height?: string | number;
  getId?: (row: any) => string | number;
  loading?: boolean;
  getRowId?: (row: any) => string | number;
  showFooter?: boolean;
  totalRow?: any; // Riga extra da aggiungere in fondo per i totali
  totalRowId?: string | number; // ID per la riga totale (default: 'total')
  customSx?: Record<string, any>; // Stili CSS personalizzati aggiuntivi
  columnBufferPx?: number;
  processRowUpdate?: (newRow: any, oldRow: any) => Promise<any> | any;
  onProcessRowUpdateError?: (error: Error) => void;
  experimentalFeatures?: Partial<GridExperimentalFeatures>;
  editable?: boolean;
  /** Abilita checkbox per selezione multipla */
  checkboxSelection?: boolean;
  /** Modello di selezione (array di id righe) */
  rowSelectionModel?: GridRowId[];
  /** Callback al cambio selezione */
  onRowSelectionModelChange?: (selection: GridRowId[]) => void;
  /** Predicato per righe selezionabili (es. escludere riga totali) */
  isRowSelectable?: (params: GridRowParams) => boolean;
  isFiltrable?: boolean;
}

export function ReadOnlyDataGrid({
  fields,
  data,
  actions = [],
  getRowActions,
  height = 'calc(100vh - 185px)',
  getId,
  loading = false,
  getRowId,
  showFooter = true,
  totalRow,
  totalRowId = 'total',
  customSx,
  columnBufferPx = undefined,
  processRowUpdate,
  onProcessRowUpdateError,
  experimentalFeatures,
  editable = false,
  checkboxSelection = false,
  rowSelectionModel,
  onRowSelectionModelChange,
  isRowSelectable: isRowSelectableProp,
  isFiltrable = false,
}: ReadOnlyDataGridProps) {
  const hasActions = Boolean(getRowActions) || actions.length > 0;
  const isRowSelectable = React.useCallback(
    (params: GridRowParams) => {
      if (params.row?._isTotalRow || (totalRow && params.id === totalRowId)) return false;
      return isRowSelectableProp?.(params) ?? true;
    },
    [totalRow, totalRowId, isRowSelectableProp]
  );
  // Stato per il menu contestuale
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    rowId: GridRowId | null;
    row: any | null;
  } | null>(null);

  // Ref per il DataGrid
  const gridRef = React.useRef<HTMLDivElement>(null);

  // Chiudi il menu
  const handleCloseContextMenu = React.useCallback(() => {
    setContextMenu(null);
  }, []);

  // Gestore per l'azione del menu
  const handleMenuAction = React.useCallback(
    (action: RowAction) => {
      if (contextMenu?.rowId && contextMenu?.row) {
        action.onClick(contextMenu.rowId, contextMenu.row);
        handleCloseContextMenu();
      }
    },
    [contextMenu, handleCloseContextMenu]
  );

  // Convert data to GridRowsProp format
  const gridRows: GridRowsProp = React.useMemo(() => {
    const rows = data.map((row, index) => {
      const gridRow: any = {
        id: getRowId ? getRowId(row) : getId ? getId(row) : row.id || index,
        // Includi tutti i campi della riga originale, non solo quelli nei fields
        // Questo permette di accedere a campi aggiuntivi come totals_data_id in renderCell
        ...row,
      };
      // Assicurati che i campi definiti nei fields abbiano la priorità
      fields.forEach(field => {
        const value = row[field.field];
        gridRow[field.field] = value ?? null;
      });
      return gridRow;
    });

    // Aggiungi la riga totale se presente
    if (totalRow) {
      const totalGridRow: any = {
        id: totalRowId,
        _isTotalRow: true, // Flag per identificare la riga totale
      };
      fields.forEach(field => {
        const value = totalRow[field.field];
        totalGridRow[field.field] = value ?? null;
      });
      rows.push(totalGridRow);
    }

    return rows;
  }, [data, fields, getId, getRowId, totalRow, totalRowId]);

  // Calcola le posizioni left per le colonne sticky
  const stickyPositions = React.useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;
    fields.forEach(field => {
      if (field.sticky === 'left') {
        const leftPosition = field.stickyLeft ?? cumulativeLeft;
        positions[field.field] = leftPosition;
        cumulativeLeft += field.width || 150;
      }
    });
    return positions;
  }, [fields]);

  const stickyLeftFields = React.useMemo(() => fields.filter(f => f.sticky === 'left'), [fields]);
  const lastStickyLeftField =
    stickyLeftFields.length > 0 ? stickyLeftFields[stickyLeftFields.length - 1].field : null;
  const hasStickyColumns = stickyLeftFields.length > 0;

  // Comparatore di default per tipo (usato quando si forza la riga totale in fondo)
  const defaultCompare = React.useCallback((v1: any, v2: any, type?: string): number => {
    const a = v1 == null ? '' : v1;
    const b = v2 == null ? '' : v2;
    if (type === 'number' || type === 'singleSelect') {
      const na = Number(a);
      const nb = Number(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    }
    if (type === 'date' || type === 'dateTime') {
      const da = new Date(a).getTime();
      const db = new Date(b).getTime();
      if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  }, []);

  // Build columns from fields config
  const columns: GridColDef[] = React.useMemo(() => {
    const fieldColumns: GridColDef[] = fields.map(field => {
      const hasCustomFormatter = typeof field.valueFormatter === 'function';
      const valueFormatter = hasCustomFormatter
        ? (params: any) => {
            if (!params) return '';
            return field.valueFormatter?.(params?.value ?? params ?? '') ?? '';
          }
        : field.type === 'number'
          ? (value: any) => {
              if (field.significantDigits != null) {
                return formatNumberItalianSignificant(value, field.significantDigits);
              }
              if (field.decimalPlaces != null) {
                return formatNumberItalian(value, { maximumFractionDigits: field.decimalPlaces });
              }
              return formatNumberItalian(value as any);
            }
          : field.type === 'dateTime' || field.type === 'date'
            ? (value: any) => {
                if (!value) return '';
                const raw = String(value);
                const normalized = /[zZ]$|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`;
                const dt = new Date(normalized);
                return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleString('it-IT');
              }
            : undefined;

      const column: GridColDef = {
        field: field.field,
        headerName: field.headerName,
        width: field.width || 150,
        type: field.type,
        align: field.align,
        headerAlign: field.headerAlign,
        valueFormatter,
        editable: editable && field.type === 'singleSelect',
      };

      // Aggiungi valueOptions per singleSelect
      if (field.type === 'singleSelect' && (field as any).valueOptions) {
        (column as any).valueOptions = (field as any).valueOptions;
      }

      // Se c'è un renderCell personalizzato, usalo
      if (field.renderCell) {
        column.renderCell = field.renderCell;
      }

      // Gestione colonne sticky
      if (field.sticky === 'left') {
        const stickyClassName = `sticky-column-${field.field}`;
        column.headerClassName = stickyClassName;
        column.cellClassName = stickyClassName;
      }

      // Riga totale sempre in fondo: escludi dal riordinamento (come colonna fissa per la riga)
      // Usare getSortComparator (usato per primo dal grid) così il nostro comparatore non viene sostituito dai default di tipo.
      if (totalRow && totalRowId != null) {
        const totalIdStr = String(totalRowId);
        column.getSortComparator = (sortDirection: GridSortDirection) => {
          return (v1: any, v2: any, cellParams1: any, cellParams2: any) => {
            const id1 = cellParams1?.id ?? cellParams1?.rowNode?.id;
            const id2 = cellParams2?.id ?? cellParams2?.rowNode?.id;
            const row1 =
              (cellParams1?.rowNode as any)?.row ?? cellParams1?.api?.getRow?.(cellParams1?.id);
            const row2 =
              (cellParams2?.rowNode as any)?.row ?? cellParams2?.api?.getRow?.(cellParams2?.id);
            const row1IsTotal =
              String(id1) === totalIdStr || (row1 as any)?._isTotalRow;
            const row2IsTotal =
              String(id2) === totalIdStr || (row2 as any)?._isTotalRow;
            // Sempre totale in fondo: ritorna 1 quando row1 è totale (row1 dopo row2), -1 quando row2 è totale.
            const baseCompare = defaultCompare(v1, v2, field.type);
            return row1IsTotal
              ? 1
              : row2IsTotal
                ? -1
                : sortDirection === 'desc'
                  ? -baseCompare
                  : baseCompare;
          };
        };
      }

      return column;
    });

    // Add actions column if actions are provided
    if (hasActions) {
      const actionsColumn: GridColDef = {
        field: 'actions',
        type: 'actions',
        headerName: 'Azioni',
        width: getRowActions ? 180 : actions.length > 2 ? 180 : actions.length > 1 ? 120 : 100,
        getActions: ({ id, row }) => {
          const rowActions = getRowActions ? getRowActions(row) : actions;
          return rowActions
            .filter(action => action.visible === undefined || action.visible(row))
            .map(action => (
              <GridActionsCellItem
                key={action.label}
                icon={
                  <Tooltip title={action.label} placement="top">
                    {action.icon}
                  </Tooltip>
                }
                label={action.label}
                onClick={() => action.onClick(id, row)}
                color={action.color || 'inherit'}
                showInMenu={action.showInMenu || false}
              />
            ));
        },
      };
      return [...fieldColumns, actionsColumn];
    }

    return fieldColumns;
  }, [fields, actions, getRowActions, hasActions, totalRow, totalRowId, defaultCompare]);

  // Funzione per esportare la tabella in CSV
  const handleExportCSV = React.useCallback(() => {
    // Filtra le righe totali se presenti
    const dataToExport = gridRows.filter(row => !row._isTotalRow);
    const csvContent = convertToCSV(dataToExport, fields);
    downloadCSV(csvContent, 'export');
    handleCloseContextMenu();
  }, [gridRows, fields, handleCloseContextMenu]);

  // Gestore per il click destro sulla riga (dopo che gridRows è definito)
  React.useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      if (!gridRef.current) return;

      // Verifica se il click è dentro la griglia
      const target = event.target as HTMLElement;
      const rowElement = target.closest('.MuiDataGrid-row');
      const gridElement = target.closest('.MuiDataGrid-root');

      if (rowElement || gridElement) {
        event.preventDefault();
        // MUI DataGrid usa data-id sull'elemento della riga
        const rowIdAttr = rowElement?.getAttribute('data-id');
        const row = rowIdAttr ? gridRows.find(r => String(r.id) === rowIdAttr) : null;

        setContextMenu({
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
          rowId: row?.id || null,
          row: row,
        });
      }
    };

    const gridElement = gridRef.current;
    if (gridElement) {
      gridElement.addEventListener('contextmenu', handleContextMenu);
      return () => {
        gridElement.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, [gridRows]);

  return (
    <Box
      ref={gridRef}
      sx={{
        height,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <DataGrid
        showToolbar={isFiltrable}
        getRowHeight={() => 36}
        columnHeaderHeight={36}
        sx={{
          borderRadius: 2,
          // Stile per la riga totale: bordo superiore più spesso
          '& .MuiDataGrid-row.total-row': {
            backgroundColor: 'background.paper',
          },
          // Stili per colonne sticky (z-index sopra le celle normali; ombra sull'ultima)
          ...fields.reduce(
            (acc, field) => {
              if (field.sticky === 'left') {
                const stickyClassName = `sticky-column-${field.field}`;
                const leftPosition = stickyPositions[field.field] ?? 0;
                const isLastSticky = field.field === lastStickyLeftField;
                const shadow = isLastSticky ? { boxShadow: '2px 0 4px rgba(0,0,0,0.06)' } : {};

                acc[`& .MuiDataGrid-cell.${stickyClassName}`] = {
                  position: 'sticky',
                  left: `${leftPosition}px`,
                  zIndex: 125,
                  backgroundColor: 'background.paper',
                  ...shadow,
                };
                acc[`& .MuiDataGrid-columnHeader.${stickyClassName}`] = {
                  position: 'sticky',
                  left: `${leftPosition}px`,
                  zIndex: 126,
                  backgroundColor: 'background.paper',
                  ...shadow,
                };
              }
              return acc;
            },
            {} as Record<string, any>
          ),
          // Stili personalizzati aggiuntivi
          ...(customSx || {}),
        }}
        rows={gridRows}
        columnBufferPx={hasStickyColumns ? 10000 : columnBufferPx}
        columns={columns}
        loading={loading}
        // La DataGrid MIT impagina sempre in modalità client con max 100 righe per pagina:
        // le righe oltre la 100ª (e la riga TOTALE, ordinata in fondo) restavano nascoste.
        // Con paginationMode="server" la griglia non taglia le righe ricevute: le mostra tutte
        // (scroll verticale virtualizzato), quindi il paginatore viene nascosto.
        paginationMode="server"
        rowCount={gridRows.length}
        hideFooter={!showFooter}
        hideFooterPagination
        hideFooterSelectedRowCount={!showFooter}
        // La riga TOTALE non è mai modificabile
        isCellEditable={params => !params.row?._isTotalRow && params.id !== totalRowId}
        disableRowSelectionOnClick={!checkboxSelection}
        checkboxSelection={checkboxSelection}
        {...(checkboxSelection && {
          rowSelectionModel:
            rowSelectionModel != null
              ? { type: 'include' as const, ids: new Set(rowSelectionModel) }
              : undefined,
          onRowSelectionModelChange: (model: {
            type: 'include' | 'exclude';
            ids: Set<GridRowId>;
          }) => {
            const selectedIds: GridRowId[] =
              model.type === 'include'
                ? Array.from(model.ids)
                : gridRows
                    .filter(r => !r._isTotalRow && r.id !== totalRowId && !model.ids.has(r.id))
                    .map(r => r.id);
            onRowSelectionModelChange?.(selectedIds);
          },
          isRowSelectable: isRowSelectable,
        })}
        disableColumnMenu={!isFiltrable}
        disableColumnFilter={!isFiltrable}
        disableColumnSelector={true}
        rowBufferPx={10000}
        getRowId={getRowId}
        getRowClassName={(params: any) => {
          // Aggiungi classe per la riga totale
          if (params.row._isTotalRow || params.id === totalRowId) {
            return 'total-row';
          }
          return '';
        }}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        {...(experimentalFeatures && { experimentalFeatures })}
      />
      {(hasActions || true) && (
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          {hasActions && contextMenu?.row && (
            <>
              {(getRowActions ? getRowActions(contextMenu.row) : actions)
                .filter(
                  action =>
                    action.visible === undefined ||
                    (contextMenu?.row && action.visible(contextMenu.row))
                )
                .map((action, index) => (
                  <MenuItem key={index} onClick={() => handleMenuAction(action)}>
                    <ListItemIcon>{action.icon}</ListItemIcon>
                    <ListItemText>{action.label}</ListItemText>
                  </MenuItem>
                ))}
            </>
          )}
          {hasActions && contextMenu?.row && <Divider />}
          <MenuItem onClick={handleExportCSV}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Esporta</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
}
